import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { DashboardLayout } from "@/projects/components/dashboard-layout";
import { useSubmissions, useAuth } from "@/projects/lib/useStore";
import { verifySubmission } from "@/projects/lib/store";
import { Card } from "@/projects/components/ui/card";
import { Button } from "@/projects/components/ui/button";
import { Textarea } from "@/projects/components/ui/textarea";
import { Label } from "@/projects/components/ui/label";
import { ArrowLeft, Trophy, AlertTriangle, FileVideo } from "lucide-react";
import { cn } from "@/projects/lib/utils";
import { useState } from "react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/projects/components/ui/alert-dialog";
import { StatusBadge } from "@/projects/components/status-badge";
import { AiFeedbackAssistant } from "@/projects/components/ai-feedback-assistant";

export const Route = createFileRoute("/teacher/verify/$id")({
  head: () => ({ meta: [{ title: "Verify Submission — Teacher" }] }),
  component: VerifyPage,
});

function VerifyPage() {
  const { id } = Route.useParams();
  const auth = useAuth();
  const submissions = useSubmissions();
  const navigate = useNavigate();
  const s = submissions.find((x) => x.id === id);

  const [choice, setChoice] = useState<"Not Proper" | "Ready for Reward" | null>(
    s?.status === "Not Proper" || s?.status === "Ready for Reward" ? s.status : null,
  );
  const [remarks, setRemarks] = useState(s?.teacherRemarks ?? "");
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!s) {
    return (
      <DashboardLayout role="teacher" title="Not found">
        <div className="rounded-xl border bg-card p-8 text-center">
          <div className="text-lg font-semibold">Submission not found</div>
          <Link to="/teacher/submissions"><Button variant="outline" className="mt-4">Back</Button></Link>
        </div>
      </DashboardLayout>
    );
  }

  const doSave = () => {
    if (!choice) { toast.error("Please choose a verification status."); return; }
    if (choice === "Not Proper" && !remarks.trim()) {
      toast.error("Please mention the reason why the video is not proper.");
      return;
    }
    verifySubmission(s.id, choice, remarks.trim(), auth?.name ?? "Teacher");
    toast.success("Verification saved successfully");
    navigate({ to: "/teacher/submissions" });
  };

  const handleSubmit = () => {
    if (choice === "Ready for Reward") setConfirmOpen(true);
    else doSave();
  };

  return (
    <DashboardLayout role="teacher" title="Verify Submission" breadcrumb={["Home", "Assignments", s.id]}>
      <Link to="/teacher/submissions" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to Assigned Submissions
      </Link>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2 p-5">
          <div className="aspect-video overflow-hidden rounded-lg bg-black">
            <video controls className="h-full w-full" poster="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200">
              <source src="https://cdn.coverr.co/videos/coverr-a-teacher-in-a-classroom-8747/1080p.mp4" type="video/mp4" />
            </video>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-royal-soft text-royal"><FileVideo className="h-4 w-4" /></div>
              <div>
                <div className="font-medium">{s.fileName}</div>
                <div className="text-xs text-muted-foreground">{s.fileSizeMB} MB</div>
              </div>
            </div>
            <StatusBadge status={s.status} />
          </div>

          <div className="mt-6">
            <div className="mb-2 text-sm font-semibold">Verification Status</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <StatusChoice
                selected={choice === "Ready for Reward"}
                color="success"
                icon={Trophy}
                title="Ready for Reward"
                desc="Video meets criteria and deserves recognition."
                onClick={() => setChoice("Ready for Reward")}
              />
              <StatusChoice
                selected={choice === "Not Proper"}
                color="destructive"
                icon={AlertTriangle}
                title="Not Proper"
                desc="Video does not meet the required standard."
                onClick={() => setChoice("Not Proper")}
              />
            </div>

            <div className="mt-4">
              <AiFeedbackAssistant
                studentName={s.studentName}
                videoTitle={s.videoTitle}
                decision={choice}
                notes={remarks}
                onApply={(t) => setRemarks(t)}
              />
            </div>

            <div className="mt-4">
              <Label>Teacher Remarks {choice === "Not Proper" && <span className="text-destructive">*</span>}</Label>
              <Textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder={choice === "Not Proper" ? "Mention the reason why the video is not proper" : "Optional remarks…"}
                rows={4}
              />
            </div>


            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Link to="/teacher/submissions"><Button variant="outline">Cancel</Button></Link>
              <Button onClick={handleSubmit}>Save Verification</Button>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="text-sm font-semibold">Submission Details</div>
          <div className="mt-3 space-y-2 text-sm">
            <Row k="Reference" v={<span className="font-mono text-royal">{s.id}</span>} />
            <Row k="Student" v={s.studentName} />
            <Row k="Student ID" v={s.studentId} />
            <Row k="Title" v={s.videoTitle} />
            <Row k="Submitted" v={new Date(s.submittedAt).toLocaleString()} />
            <Row k="Assigned" v={s.assignedAt ? new Date(s.assignedAt).toLocaleString() : "—"} />
          </div>
        </Card>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as Ready for Reward?</AlertDialogTitle>
            <AlertDialogDescription>This will finalize the verification and notify the admin.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmOpen(false); doSave(); }}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

function StatusChoice({
  selected, color, icon: Icon, title, desc, onClick,
}: { selected: boolean; color: "success" | "destructive"; icon: React.ComponentType<{ className?: string }>; title: string; desc: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-start gap-3 rounded-xl border-2 p-4 text-left transition-all",
        selected
          ? color === "success" ? "border-success bg-success/5" : "border-destructive bg-destructive/5"
          : "border-border bg-card hover:bg-muted/50",
      )}
    >
      <div className={cn(
        "grid h-10 w-10 shrink-0 place-items-center rounded-lg",
        color === "success" ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive",
      )}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="font-semibold">{title}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
    </button>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return <div className="flex items-start justify-between gap-3"><span className="text-muted-foreground">{k}</span><span className="text-right font-medium">{v}</span></div>;
}
